export const TIPOS_AYUDA = [
  'Problema al emitir documento',
  'No puedo emitir ningún documento',
  'Problema con libros de ventas',
  'Problema con libros de compras',
  'Problema con inventario',
  'Problema con clientes',
  'Problema con reportes',
  'Acceso / usuario / contraseña',
  'Configuración',
  'Consulta comercial',
  'Otro',
]

export const TIPOS_DOCUMENTO = ['FCF', 'CCF', 'NR', 'NC', 'ND', 'CR', 'CL', 'FE', 'FSE']

export const TIPOS_CON_DOCUMENTO = [
  'Problema al emitir documento',
  'No puedo emitir ningún documento',
]

export interface WhatsAppConfig {
  ventas: string
  soporte: string
}

export interface ClienteContexto {
  cliente: string
  codigoEmpresa: string
  sucursal: string
  usuario: string
  nombreCompleto: string
  ambiente: string
}

export interface FormContacto {
  tipoAyuda: string
  mensajeAdicional: string
  tipoDocumento?: string
}

export type TipoContacto = 'ventas' | 'soporte' | 'gerencia'

const MAX_MSG_LENGTH = 1500

export const getWhatsAppConfig = (): WhatsAppConfig => ({
  ventas: process.env.REACT_APP_WHATSAPP_VENTAS || '',
  soporte: process.env.REACT_APP_WHATSAPP_SOPORTE || '',
})

export const getEmailGerencia = (): string =>
  process.env.REACT_APP_EMAIL_GERENCIA_OPERACIONES || ''

export type AmbienteTone = 'success' | 'warning' | 'info'
export type AmbienteCode = 'production' | 'testing' | 'development'

export interface AmbienteConfig {
  code: AmbienteCode
  label: string
  shortLabel: string
  description: string
  tone: AmbienteTone
}

export const getAmbienteConfig = (): AmbienteConfig => {
  const raw = process.env.REACT_APP_AMBIENTE_FACTURACION
  let code: AmbienteCode

  switch (raw) {
    case '01':
    case 'production':
      code = 'production'
      break
    case '02':
    case 'development':
      code = 'development'
      break
    case '00':
    case 'testing':
    case 'demo':
    default:
      code = 'testing'
      break
  }

  const CONFIGS: Record<AmbienteCode, AmbienteConfig> = {
    production: {
      code: 'production',
      label: 'Producción',
      shortLabel: 'PRODUCCIÓN',
      description: 'Emisión real de documentos',
      tone: 'success',
    },
    testing: {
      code: 'testing',
      label: 'Demo',
      shortLabel: 'DEMO',
      description: 'Sin efecto tributario real',
      tone: 'warning',
    },
    development: {
      code: 'development',
      label: 'Desarrollo',
      shortLabel: 'DESARROLLO',
      description: 'Entorno de desarrollo interno',
      tone: 'info',
    },
  }

  return CONFIGS[code]
}

export const getAmbiente = (): string => getAmbienteConfig().label

const formatFecha = (date: Date): string =>
  date.toLocaleDateString('es-SV', {day: '2-digit', month: '2-digit', year: 'numeric'})

const formatHora = (date: Date): string =>
  date.toLocaleTimeString('es-SV', {hour: '2-digit', minute: '2-digit', hour12: true})

const formatUsuario = (nombreCompleto: string, usuario: string): string => {
  const nombre = nombreCompleto && nombreCompleto !== 'No definido' ? nombreCompleto : ''
  return nombre ? `${nombre} (${usuario})` : usuario
}

const buildCabecera = (contexto: ClienteContexto): string => {
  const ahora = new Date()
  const {cliente, codigoEmpresa, sucursal, usuario, nombreCompleto, ambiente} = contexto
  return (
    `*Cliente:* ${cliente}\n` +
    `*Código de empresa:* ${codigoEmpresa}\n` +
    `*Sucursal:* ${sucursal}\n` +
    `*Usuario:* ${formatUsuario(nombreCompleto, usuario)}\n` +
    `*Ambiente:* ${ambiente}\n` +
    `*Fecha:* ${formatFecha(ahora)}\n` +
    `*Hora:* ${formatHora(ahora)}`
  )
}

const aplicarTruncacion = (mensajeFull: string, detalle: string): string => {
  if (mensajeFull.length <= MAX_MSG_LENGTH) return mensajeFull
  const exceso = mensajeFull.length - MAX_MSG_LENGTH
  const nuevaLongitudDetalle = detalle.length - exceso - 3
  if (nuevaLongitudDetalle <= 0) return mensajeFull.substring(0, MAX_MSG_LENGTH)
  return mensajeFull.replace(detalle, detalle.substring(0, nuevaLongitudDetalle) + '...')
}

export const buildWhatsAppMessage = (
  tipo: 'ventas' | 'soporte',
  contexto: ClienteContexto,
  form: FormContacto
): string => {
  const cabecera = buildCabecera(contexto)
  const {tipoAyuda, mensajeAdicional, tipoDocumento} = form
  const docLine =
    tipoDocumento && TIPOS_CON_DOCUMENTO.includes(tipoAyuda)
      ? `*Documento:* ${tipoDocumento}\n`
      : ''

  let mensaje: string

  switch (tipo) {
    case 'ventas':
      mensaje =
        `Hola, deseo comunicarme con el área comercial de FELES.\n\n` +
        `${cabecera}\n\n` +
        `*Consulta:* ${mensajeAdicional}\n\n` +
        `Quedo atento.`
      break

    case 'soporte':
      mensaje =
        `Hola, necesito apoyo desde FELES.\n\n` +
        `${cabecera}\n\n` +
        `*Tipo de ayuda:* ${tipoAyuda}\n` +
        `${docLine}` +
        `\n*Detalle:* ${mensajeAdicional}\n\n` +
        `Agradeceré su apoyo.`
      break
  }

  return aplicarTruncacion(mensaje, mensajeAdicional)
}

export const buildMailtoGerencia = (
  emailTo: string,
  contexto: ClienteContexto,
  form: FormContacto
): string => {
  const ahora = new Date()
  const {cliente, codigoEmpresa, sucursal, usuario, nombreCompleto, ambiente} = contexto
  const {tipoAyuda, mensajeAdicional, tipoDocumento} = form
  const docLine =
    tipoDocumento && TIPOS_CON_DOCUMENTO.includes(tipoAyuda)
      ? `Documento: ${tipoDocumento}\n`
      : ''
  const subject = `Escalamiento FELES - ${cliente} - ${tipoAyuda}`
  const body =
    `Hola,\n\n` +
    `Deseo escalar un caso con Gerencia de Operaciones.\n\n` +
    `Cliente: ${cliente}\n` +
    `Código de empresa: ${codigoEmpresa}\n` +
    `Sucursal: ${sucursal}\n` +
    `Usuario: ${formatUsuario(nombreCompleto, usuario)}\n` +
    `Ambiente: ${ambiente}\n` +
    `Fecha: ${formatFecha(ahora)}\n` +
    `Hora: ${formatHora(ahora)}\n` +
    `Tipo de ayuda: ${tipoAyuda}\n` +
    `${docLine}` +
    `Detalle: ${mensajeAdicional}\n\n` +
    `Saludos.`
  return `mailto:${emailTo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/**
 * Abre el cliente de correo predeterminado con asunto y cuerpo prellenados.
 * Usa window.location.href para evitar bloqueos de popup blockers con mailto.
 */
export const openMailtoGerencia = (
  emailTo: string,
  contexto: ClienteContexto,
  form: FormContacto
): void => {
  window.location.href = buildMailtoGerencia(emailTo, contexto, form)
}

/**
 * Abre WhatsApp en nueva pestaña con el mensaje pre-llenado.
 * onBeforeSend es un hook para fase 2 (persistencia en backend antes de abrir WA).
 */
export const openWhatsApp = async (
  phone: string,
  message: string,
  onBeforeSend?: () => Promise<void>
): Promise<void> => {
  if (onBeforeSend) await onBeforeSend()
  const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`
  window.open(url, '_blank', 'noopener,noreferrer')
}
